// client-portal/src/services/MetricTracker.js
import { doc, getDoc, updateDoc, collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../firebase/config';

/**
 * MetricTracker Service
 * Automatically tracks and calculates contractor performance metrics
 */

class MetricTracker {
    
    /**
     * Calculate all metrics for a contractor
     * @param {string} contractorId - Contractor's UID
     * @returns {Object} Calculated metrics
     */
    async calculateMetrics(contractorId) {
        try {
            // Get all jobs for this contractor
            const jobsQuery = query(
                collection(db, 'workOrders'),
                where('contractorId', '==', contractorId)
            );
            
            const jobsSnapshot = await getDocs(jobsQuery);
            const jobs = jobsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

            // Get contractor profile for join date
            const contractorRef = doc(db, 'contractors', contractorId);
            const contractorSnap = await getDoc(contractorRef);
            const contractorData = contractorSnap.exists() ? contractorSnap.data() : {};

            // Calculate metrics
            const metrics = {
                // Job Counts
                totalJobs: jobs.length,
                supervisedJobs: jobs.filter(j => j.isSupervised === true).length,
                independentJobs: jobs.filter(j => j.isSupervised === false).length,
                completedJobs: jobs.filter(j => j.status === 'completed').length,
                cancelledJobs: jobs.filter(j => j.status === 'cancelled').length,
                
                // Completion Rate
                completionRate: this.calculateCompletionRate(jobs),
                
                // Average Rating
                averageRating: this.calculateAverageRating(jobs),
                totalRatings: jobs.filter(j => j.clientApproval?.rating).length,
                
                // Time Metrics
                daysActive: this.calculateDaysActive(contractorData.createdAt),
                joinedDate: contractorData.createdAt || new Date().toISOString(),
                
                // First/Last Job Dates
                firstJobDate: this.getFirstJobDate(jobs),
                lastJobDate: this.getLastJobDate(jobs),
                
                // Last Updated
                lastCalculated: new Date().toISOString()
            };

            // Calculate eligibility for next phase
            const eligibility = this.calculateEligibility(
                metrics,
                contractorData.phase || 'trainee'
            );

            return {
                ...metrics,
                ...eligibility
            };

        } catch (error) {
            console.error('Error calculating metrics:', error);
            throw error;
        }
    }

    /**
     * Calculate completion rate
     */
    calculateCompletionRate(jobs) {
        if (jobs.length === 0) return 0;
        
        const completed = jobs.filter(j => j.status === 'completed').length;
        const total = jobs.filter(j => 
            j.status === 'completed' || j.status === 'cancelled'
        ).length;
        
        if (total === 0) return 0;
        
        return Math.round((completed / total) * 100 * 10) / 10; // Round to 1 decimal
    }

    /**
     * Calculate average rating from client approvals
     */
    calculateAverageRating(jobs) {
        const ratingsJobs = jobs.filter(j => j.clientApproval?.rating);
        
        if (ratingsJobs.length === 0) return 0;
        
        const sum = ratingsJobs.reduce((acc, job) => acc + job.clientApproval.rating, 0);
        const avg = sum / ratingsJobs.length;
        
        return Math.round(avg * 10) / 10; // Round to 1 decimal
    }

    /**
     * Calculate days active since joining
     */
    calculateDaysActive(joinedDate) {
        if (!joinedDate) return 0;
        
        const joined = new Date(joinedDate);
        const now = new Date();
        const diffTime = Math.abs(now - joined);
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        
        return diffDays;
    }

    /**
     * Get first job date
     */
    getFirstJobDate(jobs) {
        if (jobs.length === 0) return null;
        
        const sorted = jobs
            .filter(j => j.createdAt)
            .sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));
        
        return sorted.length > 0 ? sorted[0].createdAt : null;
    }

    /**
     * Get last job date
     */
    getLastJobDate(jobs) {
        if (jobs.length === 0) return null;
        
        const sorted = jobs
            .filter(j => j.createdAt)
            .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
        
        return sorted.length > 0 ? sorted[0].createdAt : null;
    }

    /**
     * Calculate eligibility for promotion based on phase
     */
    calculateEligibility(metrics, currentPhase) {
        const criteria = this.getPromotionCriteria(currentPhase);
        
        if (!criteria) {
            return {
                eligibleForPromotion: false,
                nextPhase: null,
                criteriaStatus: {}
            };
        }

        const criteriaStatus = {};
        let allMet = true;

        // Check each criterion
        Object.keys(criteria).forEach(key => {
            const required = criteria[key];
            const current = metrics[key] || 0;
            const met = current >= required;

            criteriaStatus[key] = {
                current,
                required,
                met
            };

            if (!met) allMet = false;
        });

        return {
            eligibleForPromotion: allMet,
            nextPhase: this.getNextPhase(currentPhase),
            criteriaStatus,
            requiresAdminApproval: true // All promotions require admin approval
        };
    }

    /**
     * Get promotion criteria for each phase
     */
    getPromotionCriteria(currentPhase) {
        const criteria = {
            'shadow': null, // Admin only decision
            
            'trainee': {
                supervisedJobs: 10,
                completionRate: 90,
                daysActive: 14
            },
            
            'junior': {
                independentJobs: 25,
                completionRate: 95,
                daysActive: 90,
                averageRating: 4.0
            },
            
            'standard': {
                totalJobs: 100,
                completionRate: 98,
                daysActive: 180,
                averageRating: 4.5
            },
            
            'experienced': null // Already at top
        };

        return criteria[currentPhase];
    }

    /**
     * Get next phase in progression
     */
    getNextPhase(currentPhase) {
        const progression = {
            'shadow': 'trainee',
            'trainee': 'junior',
            'junior': 'standard',
            'standard': 'experienced',
            'experienced': null
        };

        return progression[currentPhase];
    }

    /**
     * Update contractor's metrics in Firestore
     */
    async updateContractorMetrics(contractorId) {
        try {
            const metrics = await this.calculateMetrics(contractorId);
            
            await updateDoc(doc(db, 'contractors', contractorId), {
                metrics: metrics,
                updatedAt: new Date().toISOString()
            });

            console.log(`✅ Updated metrics for contractor ${contractorId}`);
            return metrics;

        } catch (error) {
            console.error('Error updating contractor metrics:', error);
            throw error;
        }
    }

    /**
     * Flag a job as supervised or training
     * @param {string} jobId - Job ID
     * @param {Object} flags - { isSupervised, isTraining, isExceptional }
     */
    async flagJob(jobId, flags) {
        try {
            await updateDoc(doc(db, 'workOrders', jobId), {
                ...flags,
                flaggedAt: new Date().toISOString(),
                updatedAt: new Date().toISOString()
            });

            // Get contractor ID from job and update their metrics
            const jobRef = doc(db, 'workOrders', jobId);
            const jobSnap = await getDoc(jobRef);
            
            if (jobSnap.exists() && jobSnap.data().contractorId) {
                await this.updateContractorMetrics(jobSnap.data().contractorId);
            }

            console.log(`✅ Flagged job ${jobId}`, flags);

        } catch (error) {
            console.error('Error flagging job:', error);
            throw error;
        }
    }

    /**
     * Promote contractor to next phase
     * @param {string} contractorId - Contractor ID
     * @param {string} newPhase - New phase
     * @param {string} adminNote - Optional admin note
     * @param {string} adminUid - Admin who approved
     */
    async promoteContractor(contractorId, newPhase, adminNote = '', adminUid = '') {
        try {
            const contractorRef = doc(db, 'contractors', contractorId);
            const contractorSnap = await getDoc(contractorRef);
            
            if (!contractorSnap.exists()) {
                throw new Error('Contractor not found');
            }

            const currentData = contractorSnap.data();
            const oldPhase = currentData.phase || 'trainee';

            // Get new hourly rate based on phase
            const newRate = this.getPhaseRate(newPhase);

            // Create promotion record
            const promotionRecord = {
                date: new Date().toISOString(),
                from: oldPhase,
                to: newPhase,
                adminNote: adminNote,
                adminUid: adminUid,
                metrics: currentData.metrics || {}
            };

            // Update contractor
            await updateDoc(contractorRef, {
                phase: newPhase,
                hourlyRate: newRate,
                promotionHistory: [
                    ...(currentData.promotionHistory || []),
                    promotionRecord
                ],
                lastPromotedAt: new Date().toISOString(),
                updatedAt: new Date().toISOString()
            });

            console.log(`✅ Promoted contractor ${contractorId} from ${oldPhase} to ${newPhase}`);

            return promotionRecord;

        } catch (error) {
            console.error('Error promoting contractor:', error);
            throw error;
        }
    }

    /**
     * Get hourly rate for phase
     */
    getPhaseRate(phase) {
        const rates = {
            'shadow': 0,
            'trainee': 18,
            'junior': 22,
            'standard': 25,
            'experienced': 27
        };

        return rates[phase] || 18;
    }

    /**
     * Get custom rate for contractor (admin override)
     */
    async setCustomRate(contractorId, customRate, adminNote = '') {
        try {
            await updateDoc(doc(db, 'contractors', contractorId), {
                customRate: customRate,
                hourlyRate: customRate,
                customRateNote: adminNote,
                customRateSetAt: new Date().toISOString(),
                updatedAt: new Date().toISOString()
            });

            console.log(`✅ Set custom rate for contractor ${contractorId}: $${customRate}/hr`);

        } catch (error) {
            console.error('Error setting custom rate:', error);
            throw error;
        }
    }
}

// Export singleton instance
export const metricTracker = new MetricTracker();
export default metricTracker;
